import React from "react";
import { Flag } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

/**
 * Pílula de prioridade do Job.
 * - Se `value` for "low" | "medium" | "high", usa o valor explícito do admin.
 * - Caso contrário, o caller pode derivar via `derivedUrgency()` baseado em
 *   scheduled_at + status (atrasado / hoje / amanhã / próximos dias).
 */
const PRIORITY_CONFIG = {
  high: {
    label: "URGENTE",
    bg: "bg-red-500",
    text: "text-white",
    icon: "text-white",
  },
  medium: {
    label: "MÉDIA",
    bg: "bg-emerald-500",
    text: "text-white",
    icon: "text-white",
  },
  low: {
    label: "BAIXA",
    bg: "bg-slate-300",
    text: "text-slate-700",
    icon: "text-slate-600",
  },
};

export function JobPriorityPill({ value, size = "md", className = "" }) {
  const { t } = useI18n();
  if (!value) return null;
  const cfg = PRIORITY_CONFIG[value];
  if (!cfg) return null;
  const sz = size === "sm" ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5";
  return (
    <span
      data-testid={`job-priority-${value}`}
      className={`inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-wide ${cfg.bg} ${cfg.text} ${sz} ${className}`}
    >
      <Flag size={size === "sm" ? 10 : 12} className={cfg.icon} fill="currentColor" />
      {t(cfg.label)}
    </span>
  );
}

/**
 * Deriva uma prioridade a partir de scheduled_at + status.
 * - finalizada/cancelada → null (sem pílula)
 * - atrasado (passou e ainda não finalizou) → high
 * - hoje → high
 * - amanhã → medium
 * - dentro de 3 dias → medium
 * - mais distante → low
 */
export function derivedUrgency(scheduledAt, status) {
  if (status === "finalizada" || status === "cancelada") return null;
  if (!scheduledAt) return null;
  try {
    const sched = new Date(scheduledAt);
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const sd = new Date(sched);
    sd.setHours(0, 0, 0, 0);
    const diffDays = Math.round((sd - today) / (1000 * 60 * 60 * 24));
    if (sched < now && status !== "andamento") return "high";
    if (diffDays === 0) return "high";
    if (diffDays === 1) return "medium";
    if (diffDays <= 3) return "medium";
    return "low";
  } catch {
    return null;
  }
}
